import { Box, Button, Flex, useToast } from '@chakra-ui/react';
import type {
  SnapshotCsvFieldId,
  SnapshotStageBucketId,
} from 'services/snapshotsService';
import { getSnapshotApiErrorMessage } from '../snapshotApiError';
import { useSnapshotCsvMapping } from '../useSnapshotCsvMapping';
import { SnapshotCsvMappingPanel } from './SnapshotCsvMappingPanel';
import { SnapshotPrimaryButton } from './snapshotUi';
import { SnapshotWizardSteps } from './SnapshotWizardSteps';

type SnapshotCsvMappingFlowProps = {
  token: string;
  file: File;
  submitLabel: string;
  isSubmitting?: boolean;
  onBack: () => void;
  onSubmit: (mappings: {
    columnMapping: Record<string, string>;
    stageMapping: Record<string, SnapshotStageBucketId>;
  }) => Promise<void>;
};

export function SnapshotCsvMappingFlow({
  token,
  file,
  submitLabel,
  isSubmitting,
  onBack,
  onSubmit,
}: SnapshotCsvMappingFlowProps) {
  const toast = useToast();
  const mapping = useSnapshotCsvMapping(token, file);

  const handleSubmit = async () => {
    const result = mapping.validate();
    if (!result.ok) {
      if (result.reason === 'columns') {
        toast({
          title: 'Map URL, Company, and Lifecycle Stage',
          status: 'warning',
        });
        return;
      }
      if (result.reason === 'stages') {
        toast({
          title: 'Map every stage to Lead, Opportunity, or Customer',
          status: 'warning',
        });
        return;
      }
      if (result.reason === 'no_rows') {
        toast({
          title: 'No accounts to import',
          description: 'Fix or remove empty and duplicate rows in your CSV.',
          status: 'warning',
        });
        return;
      }
      return;
    }

    try {
      await onSubmit({
        columnMapping: result.columnMapping,
        stageMapping: result.stageMapping,
      });
    } catch (err) {
      toast({
        title: getSnapshotApiErrorMessage(err, 'Upload failed'),
        status: 'error',
      });
      throw err;
    }
  };

  return (
    <Box>
      <SnapshotWizardSteps current="columns" />
      <SnapshotCsvMappingPanel
        file={file}
        preview={mapping.preview}
        loading={mapping.loading}
        columnMapping={mapping.columnMapping}
        onAssignColumn={(header, fieldId) =>
          mapping.assignColumn(header, fieldId as SnapshotCsvFieldId | '')
        }
        stageMapping={mapping.stageMapping}
        onStageMappingChange={(value, bucket) =>
          mapping.setStageMapping((prev) => ({ ...prev, [value]: bucket }))
        }
      />
      <Flex justify="space-between" mt={8} gap={3} flexWrap="wrap">
        <Button variant="outline" onClick={onBack} isDisabled={isSubmitting}>
          Back
        </Button>
        <SnapshotPrimaryButton
          isLoading={isSubmitting || mapping.loading}
          onClick={handleSubmit}
        >
          {submitLabel}
        </SnapshotPrimaryButton>
      </Flex>
    </Box>
  );
}
